import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';

// Import sub-components
import UserManagement from '../components/admin/UserManagement';
import StatusWorkflow from '../components/admin/StatusWorkflow';
import MediaMaster from '../components/admin/MediaMaster';
import ClientManagement from '../components/admin/ClientManagement';
import EditionsPositions from '../components/admin/EditionsPositions';
import BulkUpload from '../components/admin/BulkUpload';

const tabs = [
  { id: 'Users', label: 'Users', icon: '👥' },
  { id: 'Status Workflow', label: 'Status Workflow', icon: '🔄' },
  { id: 'Publications', label: 'Publications', icon: '📺' },
  { id: 'Clients', label: 'Clients', icon: '🏢' },
  { id: 'Editions', label: 'Editions', icon: '📖' },
  { id: 'Positions', label: 'Positions', icon: '📍' },
  { id: 'Bulk Upload', label: 'Bulk Upload', icon: '📤' },
];

export default function AdminConfigPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('Users');
  const [users, setUsers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [resources, setResources] = useState([]);
  const [clients, setClients] = useState([]);
  const [editions, setEditions] = useState([]);
  const [positions, setPositions] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [userFilters, setUserFilters] = useState({
    search: '',
    role: '',
    status: ''
  });
  const [userPage, setUserPage] = useState(1);
  const usersPerPage = 10;

  const fetchData = async () => {
    try {
      const [usersRes, statusesRes, resourcesRes, editionsRes, positionsRes, clientsRes] = await Promise.all([
        api.get('/users'),
        api.get('/statuses'),
        api.get('/master/media-resources'),
        api.get('/master/editions'),
        api.get('/master/positions'),
        api.get('/master/clients'),
      ]);
      setUsers(usersRes.data.users || []);
      setStatuses(statusesRes.data.statuses || []);
      setResources(resourcesRes.data.resources || []);
      setEditions(editionsRes.data.editions || []);
      setPositions(positionsRes.data.positions || []);
      setClients(clientsRes.data.clients || []);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAddUser = () => {
    navigate('/admin/users/create');
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await api.delete(`/users/${userId}`);
      fetchData();
    } catch (error) {
      alert('Failed to delete user');
    }
  };

  const handleBulkDeleteUsers = async () => {
    if (!selectedUsers.length) return;
    if (!window.confirm(`Delete ${selectedUsers.length} users?`)) return;
    try {
      await Promise.all(selectedUsers.map(id => api.delete(`/users/${id}`)));
      setSelectedUsers([]);
      fetchData();
    } catch (error) {
      alert('Failed to delete some users');
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const handleViewUser = (userId) => {
    navigate(`/admin/users/view/${userId}`);
  };

  const handleEditUser = (userId) => {
    navigate(`/admin/users/edit/${userId}`);
  };

  const handleBulkUpload = async (type, file, extraParams = {}) => {
    if (!file) return;
    setUploading(true);
    setUploadResult(null);
    const formData = new FormData();
    formData.append('file', file);
    
    // Append any extra parameters (like mediaType)
    Object.keys(extraParams).forEach(key => {
      formData.append(key, extraParams[key]);
    });

    try {
      const { data } = await api.post(`/master/bulk/${type}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadResult({ type, results: data.results });
      fetchData(); // Refresh data after upload
    } catch (error) {
      setUploadResult({ type, error: error.response?.data?.message || 'Upload failed' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="admin-config-page">
      <header className="admin-header">
        <div>
          <h2>Admin Configuration</h2>
          <p>Manage users, workflows, and publications data</p>
        </div>
        <div style={{ display: 'flex', gap: '1rem' }}>
          {activeTab === 'Users' && selectedUsers.length > 0 && (
            <button 
              className="btn-secondary" 
              onClick={handleBulkDeleteUsers}
              style={{ color: '#ef4444', borderColor: '#fecaca' }}
            >
              🗑️ Delete Selected ({selectedUsers.length})
            </button>
          )}
          <button className="btn-primary" onClick={handleAddUser} style={{ padding: '0.6rem 1.2rem', borderRadius: '0.5rem' }}>
            + Add New User
          </button>
        </div>
      </header>

      <div className="admin-tabs-container">
        {tabs.map((tab) => (
          <div
            key={tab.id}
            className={`admin-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span style={{ marginRight: '0.5rem' }}>{tab.icon}</span>
            {tab.label}
          </div>
        ))}
      </div>

      <div className="admin-content-card">
        {activeTab === 'Users' && (
          <UserManagement 
            users={users}
            userFilters={userFilters}
            setUserFilters={setUserFilters}
            userPage={userPage}
            setUserPage={setUserPage}
            usersPerPage={usersPerPage}
            selectedUsers={selectedUsers}
            setSelectedUsers={setSelectedUsers}
            toggleUserSelection={toggleUserSelection}
            handleViewUser={handleViewUser}
            handleEditUser={handleEditUser}
            handleDeleteUser={handleDeleteUser}
          />
        )}

        {activeTab === 'Status Workflow' && (
          <StatusWorkflow statuses={statuses} onRefresh={fetchData} />
        )}

        {activeTab === 'Publications' && (
          <MediaMaster resources={resources} onRefresh={fetchData} />
        )}

        {activeTab === 'Clients' && (
          <ClientManagement clients={clients} onRefresh={fetchData} />
        )}

        {activeTab === 'Editions' && (
          <EditionsPositions 
            editions={editions} 
            positions={positions} 
            activeTab="Editions" 
            onRefresh={fetchData}
            resources={resources}
          />
        )}

        {activeTab === 'Positions' && (
          <EditionsPositions 
            editions={editions} 
            positions={positions} 
            activeTab="Positions" 
            onRefresh={fetchData}
            resources={resources}
          />
        )}

        {activeTab === 'Bulk Upload' && (
          <BulkUpload 
            uploading={uploading}
            uploadResult={uploadResult}
            handleBulkUpload={handleBulkUpload}
          />
        )}
      </div>
    </div>
  );
}
